// src/Pages/ApiKeys/OrgApiKeySection.tsx

import type React from "react";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button, Badge } from "@tremor/react";
import { KeyIcon, PlusIcon } from "lucide-react";
import { useApiKeys } from "@/hooks/apiKeys/useApiKeys";
import CreateApiKeyModal from "./CreateApiKeyModal";
import ApiKeySuccessModal from "./ApiKeySuccessModal";
import ApiKeyList from "./ApiKeyList";
import type { ApiKeyCreateRequest } from "@/types/apiKeys";

interface OrgApiKeySectionProps {
  orgId: number;
  orgName: string;
  isAdmin: boolean;
}

const OrgApiKeySection: React.FC<OrgApiKeySectionProps> = ({
  orgId,
  isAdmin,
}) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [newKeyDetails, setNewKeyDetails] = useState<{ key: string; name: string } | null>(null);

  const {
    keys,
    loading,
    error,
    createKey,
    toggleKeyStatus,
    revokeKey,
    clearCreatedKey,
  } = useApiKeys({
    orgId,
    autoLoad: true,
  });

  if (!isAdmin) {
    return null;
  }

  const handleCreateKey = async (data: ApiKeyCreateRequest) => {
    try {
      const response = await createKey(data);
      setNewKeyDetails({
        key: response.data.key,
        name: response.data.name,
      });
      setCreateModalOpen(false);
      setSuccessModalOpen(true);
    } catch (error) {
      // Error is handled in the hook
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
    setNewKeyDetails(null);
    clearCreatedKey();
  };

  const activeKeysCount = keys.filter(key => key.is_active).length;
  const canCreateMore = activeKeysCount < 10;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <KeyIcon className="h-5 w-5" />
              <span>API Keys</span>
            </CardTitle>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Badge color={activeKeysCount >= 8 ? "red" : activeKeysCount >= 5 ? "yellow" : "green"}>
                  {activeKeysCount}/10 Ενεργά Keys
                </Badge>
                {!canCreateMore && (
                  <Badge color="red">Μέγιστο όριο</Badge>
                )}
              </div>
              <Button
                onClick={() => setCreateModalOpen(true)}
                icon={PlusIcon}
                size="xs"
                color="blue"
                disabled={!canCreateMore}
                tooltip={!canCreateMore ? "Έχετε φτάσει το μέγιστο όριο των 10 ενεργών API keys" : undefined}
              >
                Νέο API Key
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {error && (
            <div className="bg-red-50 border-t border-red-200 px-4 py-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          
          {loading ? (
            <div className="p-6 text-center text-gray-500">
              Φόρτωση API keys...
            </div>
          ) : keys.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <KeyIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Δεν υπάρχουν API keys για αυτόν τον οργανισμό</p>
              <p className="text-sm mt-2">
                Δημιουργήστε το πρώτο API key για να ενεργοποιήσετε την προγραμματική πρόσβαση
              </p>
              {canCreateMore && (
                <Button
                  onClick={() => setCreateModalOpen(true)}
                  icon={PlusIcon}
                  color="blue"
                  className="mt-4"
                  size="xs"
                >
                  Δημιουργία Πρώτου API Key
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <ApiKeyList
                keys={keys}
                onToggleStatus={toggleKeyStatus}
                onRevoke={revokeKey}
                loading={loading}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardContent className="p-4">
          <div className="text-sm text-gray-600 space-y-2">
            <p>
              <strong>API Keys επιτρέπουν προγραμματική πρόσβαση</strong> στα δεδομένα του οργανισμού.
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Κάθε οργανισμός μπορεί να έχει μέχρι 10 ενεργά API keys</li>
              <li>Τα keys δεν λήγουν αυτόματα</li>
              <li>Μπορείτε να απενεργοποιήσετε ή να διαγράψετε keys ανά πάσα στιγμή</li>
              <li>Το πλήρες API key εμφανίζεται μόνο μία φορά κατά τη δημιουργία</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Create API Key Modal */}
      <CreateApiKeyModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateKey}
        preSelectedOrgId={orgId}
        loading={loading}
      />

      {/* Success Modal (shows the full key once) */}
      {newKeyDetails && (
        <ApiKeySuccessModal
          open={successModalOpen}
          apiKey={newKeyDetails.key}
          keyName={newKeyDetails.name}
          onClose={handleCloseSuccessModal}
        />
      )}
    </div>
  );
};

export default OrgApiKeySection;